/**
 * 《纵横杯跟场诊断战报》导出模块（纯函数，无 React / DOM 依赖）。
 *
 * 该文件被教师端 UI、复盘台与后续真实 `P2PRoomBackend.exportReport()` 复用：
 * - `buildMatchReportMarkdown` 只做「领域状态 → Markdown」的确定性转换；
 * - 唯一的 DOM 访问集中在 `downloadMarkdown` / `copyMarkdown`，且都做了环境守卫
 *   （jsdom / SSR / 剪贴板不可用时安全降级）。
 *
 * 设计约束：不修改传入的 motion / state，不读取全局时钟做业务判断
 * （仅 `generatedAt` 用于「生成时间」），同一输入必然得到同一输出。
 */
import {
  ARCHETYPE_LABEL,
  SANDBOX_LABEL,
  SIDES,
  sideLabel,
  type Commitment,
  type ConstructiveStrategyCard,
  type CrossExTaskCard,
  type MandatoryEvidence,
  type MatchPhase,
  type MotionPackage,
  type RevealedCard,
  type RoomAnnotation,
  type SandboxCard,
  type Side,
} from '@zongheng/core-schema';
import type { ArenaUiState } from '../state/types.ts';
import type { StagePhase } from '../machines/contract.ts';

/* ------------------------------------------------------------------ */
/* 文案映射                                                            */
/* ------------------------------------------------------------------ */

export const MATCH_PHASE_LABEL: Record<MatchPhase, string> = {
  LOBBY: '候场（未开始）',
  RUNNING: '进行中',
  STAGE_ENDED: '环节结束',
  PAUSED: '已暂停',
  FINISHED: '已结束',
};

export const STAGE_PHASE_LABEL: Record<StagePhase, string> = {
  PREP: '静默读卡',
  SPEAKING: '发言 / 计时',
  ENDED: '本环节结束',
};

export const SEVERITY_LABEL: Record<string, string> = {
  INFO: '提示',
  WARN: '警告',
  VIOLATION: '违规',
};

export const CROSS_EX_MODE_LABEL: Record<'TARGETED_COUNTER' | 'BLIND_RECON', string> = {
  TARGETED_COUNTER: '定向克制',
  BLIND_RECON: '全盲侦察',
};

/* ------------------------------------------------------------------ */
/* 基础格式化与转义                                                    */
/* ------------------------------------------------------------------ */

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** 毫秒 → `mm:ss` 或 `h:mm:ss`（用于时长，向下取整） */
export function formatDurationMs(ms: number): string {
  const safe = Number.isFinite(ms) ? Math.max(0, Math.floor(ms)) : 0;
  const totalSeconds = Math.floor(safe / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

/** 毫秒 → `mm:ss`（用于剩余时间，向上取整，最后一秒显示 00:01） */
export function formatClockMs(ms: number): string {
  const safe = Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 1000)) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

/** epoch 毫秒 → `YYYY-MM-DD HH:mm:ss`（本地时区，空值返回 —） */
export function formatTimestamp(epoch: number | null | undefined): string {
  if (!epoch || !Number.isFinite(epoch)) return '—';
  const date = new Date(epoch);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(
    date.getHours(),
  )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

/** epoch 毫秒 → `HH:mm:ss`（用于批注/告警时间线） */
export function formatTimeOfDay(epoch: number): string {
  if (!Number.isFinite(epoch)) return '—';
  const date = new Date(epoch);
  if (Number.isNaN(date.getTime())) return '—';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

/**
 * Markdown 行内转义：把用户可控文本（批注、告警片段、参与者姓名等）压成单行并转义，
 * 防止意外改变战报结构或注入 HTML。空值返回空串。
 */
export function escapeMarkdownInline(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ')
    // eslint-disable-next-line no-useless-escape
    .replace(/([\\`*_{}[\]()#+!|<>~])/g, '\\$1');
}

/** 表格单元格：转义 + 空值占位 */
export function markdownCell(value: unknown): string {
  const escaped = escapeMarkdownInline(value);
  return escaped.length > 0 ? escaped : '—';
}

/** 哈希缩写（保留前 12 位） */
export function shortHash(hash: string | null | undefined, length = 12): string {
  const safe = (hash ?? '').trim();
  if (!safe) return '—';
  return safe.length <= length ? safe : `${safe.slice(0, length)}…`;
}

/* ------------------------------------------------------------------ */
/* Commit-Reveal 校验状态                                              */
/* ------------------------------------------------------------------ */

export type RevealVerificationStatus = 'verified' | 'mismatch' | 'demo' | 'unverifiable';

export interface RevealVerification {
  status: RevealVerificationStatus;
  /** true = 校验通过；false = 校验失败；null = 无法/未做校验 */
  verified: boolean | null;
  label: string;
  detail: string;
}

/**
 * 判定一张已揭示卡片的 Commit-Reveal 校验状态。
 *
 * 判定优先级：
 * 1. 后端显式携带 `verified` 布尔值 → 直接采用；
 * 2. `hash` 以 `demo-` 开头（Mock/演示数据） → 演示数据，不做真实校验；
 * 3. 缺少揭示 hash 或承诺 hash → 无法校验；
 * 4. 承诺 hash 与揭示 hash 字段一致 → ✓；不一致 → ✗（仅字段比对，不在本地重算 SHA-256）。
 */
export function describeRevealVerification(
  reveal: Pick<RevealedCard, 'hash' | 'pairId' | 'side' | 'stageIndex'>,
  commitment?: Pick<Commitment, 'hash'> | null,
): RevealVerification {
  const maybeVerified = (reveal as { verified?: unknown }).verified;
  if (typeof maybeVerified === 'boolean') {
    return maybeVerified
      ? {
          status: 'verified',
          verified: true,
          label: '✓ 哈希校验通过',
          detail: '后端已校验承诺哈希与揭示内容一致',
        }
      : {
          status: 'mismatch',
          verified: false,
          label: '✗ 哈希校验失败',
          detail: '后端标记该揭示无法通过承诺哈希校验',
        };
  }

  const hash = (reveal.hash ?? '').trim();
  if (!hash) {
    return {
      status: 'unverifiable',
      verified: null,
      label: '无法校验（缺少揭示哈希）',
      detail: '该揭示未携带 hash 字段，无法与承诺记录比对',
    };
  }
  if (hash.startsWith('demo-')) {
    return {
      status: 'demo',
      verified: null,
      label: '演示数据·未做真实哈希校验',
      detail: `Mock/演示后端使用可读占位哈希（${hash}），不参与 SHA-256 校验`,
    };
  }
  if (!commitment?.hash) {
    return {
      status: 'unverifiable',
      verified: null,
      label: '无法校验（缺少承诺哈希）',
      detail: `揭示哈希 ${shortHash(hash)} 存在，但未找到对应承诺记录`,
    };
  }
  if (commitment.hash === hash) {
    return {
      status: 'verified',
      verified: true,
      label: '✓ 承诺与揭示哈希一致',
      detail: `承诺与揭示 hash 字段一致（${shortHash(hash)}），未在本地重算 SHA-256`,
    };
  }
  return {
    status: 'mismatch',
    verified: false,
    label: '✗ 承诺与揭示哈希不一致',
    detail: `承诺 ${shortHash(commitment.hash)} ≠ 揭示 ${shortHash(hash)}`,
  };
}

/* ------------------------------------------------------------------ */
/* 揭示卡片摘要（战报与复盘 UI 共用）                                   */
/* ------------------------------------------------------------------ */

export type RevealedCardKind = 'CONSTRUCTIVE' | 'CROSS_EXAM' | 'SANDBOX';

export interface RevealedCardSummary {
  kind: RevealedCardKind;
  kindLabel: string;
  title: string;
  cardId: string;
  targetRole?: string;
  archetypeLabel?: string;
  coreMission?: string;
  modeLabel?: string;
  intelHint?: string;
  tabooWarning?: string;
  evidenceTitles: string[];
  evidenceQuotes: string[];
  questionChain?: { step1LockPremise: string; step2ForceDilemma: string; step3Crystallize: string };
  sandbox?: {
    label: string;
    topicLabel: string;
    crossfireQuestion: string;
    outline: { turn: number; header: string; keyClaim: string; counterToOpponent?: string }[];
    mustHitEvidenceTitles: string[];
  };
  hiddenTrap?: { triggerCondition: string; counterResponse: string };
}

/** 把揭示卡片压成 UI / 战报需要的展示摘要（纯函数） */
export function summarizeRevealedCard(reveal: RevealedCard): RevealedCardSummary {
  const dealt = reveal.card;
  if (dealt.kind === 'CONSTRUCTIVE') {
    const card: ConstructiveStrategyCard = dealt.card;
    return {
      kind: 'CONSTRUCTIVE',
      kindLabel: '申论策略卡',
      title: card.title,
      cardId: card.cardId,
      targetRole: card.targetRole,
      archetypeLabel: ARCHETYPE_LABEL[card.archetype] ?? card.archetype,
      coreMission: card.coreMission,
      evidenceTitles: card.mandatoryEvidences.map((evidence) => evidence.shortTitle),
      evidenceQuotes: card.mandatoryEvidences.map((evidence) => evidence.exactQuoteOrStats),
      hiddenTrap: card.hiddenCounterTrap,
    };
  }
  if (dealt.kind === 'CROSS_EXAM') {
    const card: CrossExTaskCard = dealt.card;
    const modeLabel = CROSS_EX_MODE_LABEL[card.mode] ?? card.mode;
    return {
      kind: 'CROSS_EXAM',
      kindLabel: '质询任务卡',
      title: `${card.targetRole}质询任务（${modeLabel}）`,
      cardId: card.cardId,
      targetRole: card.targetRole,
      modeLabel,
      intelHint: card.intelHint,
      tabooWarning: card.tabooWarning,
      evidenceTitles: [],
      evidenceQuotes: [],
      questionChain: card.questionChain,
    };
  }
  const card: SandboxCard = dealt.card;
  const perspective = card.perspectives[reveal.side] ?? card.perspectives.AFF;
  return {
    kind: 'SANDBOX',
    kindLabel: '沙盘大纲',
    title: card.title,
    cardId: card.cardId,
    evidenceTitles: [],
    evidenceQuotes: [],
    sandbox: {
      label: perspective?.label ?? sideLabel(reveal.side),
      topicLabel: SANDBOX_LABEL[card.topic] ?? card.topic,
      crossfireQuestion: card.crossfireQuestion,
      outline: (perspective?.threeTurnOutline ?? []).map((turn) => ({
        turn: turn.turn,
        header: turn.header,
        keyClaim: turn.keyClaim,
        counterToOpponent: turn.counterToOpponent,
      })),
      mustHitEvidenceTitles: perspective?.mustHitEvidenceIds ?? [],
    },
  };
}

/* ------------------------------------------------------------------ */
/* 必打论据覆盖度                                                      */
/* ------------------------------------------------------------------ */

export interface EvidenceCoverageItem {
  evidenceId: string;
  shortTitle: string;
  exactQuoteOrStats: string;
  /** 该论据来自哪张卡片（用于教师对照） */
  sources: string[];
}

export interface SideEvidenceCoverage {
  side: Side;
  required: EvidenceCoverageItem[];
  checkedEvidenceIds: string[];
  requiredCount: number;
  hitCount: number;
  /** 0~100 的整数覆盖率 */
  percent: number;
  missing: EvidenceCoverageItem[];
}

function evidenceItem(
  map: Map<string, EvidenceCoverageItem>,
  evidence: MandatoryEvidence,
  source: string,
): void {
  const current = map.get(evidence.id);
  if (current) {
    if (!current.sources.includes(source)) current.sources.push(source);
    return;
  }
  map.set(evidence.id, {
    evidenceId: evidence.id,
    shortTitle: evidence.shortTitle,
    exactQuoteOrStats: evidence.exactQuoteOrStats,
    sources: [source],
  });
}

/**
 * 计算双方「必打论据」覆盖度。
 *
 * 分母口径：本题库所有申论卡 `mandatoryEvidences`（按卡片所属环节的发言方归属阵营）
 * + 所有沙盘视角 `mustHitEvidenceIds`，同阵营内按 evidenceId 去重。
 * 分子口径：教师端 `manualEvidenceChecks` 中已勾选的 evidenceId。
 */
export function buildEvidenceCoverage(
  motion: MotionPackage,
  manualEvidenceChecks: Record<Side, string[]>,
): Record<Side, SideEvidenceCoverage> {
  const maps: Record<Side, Map<string, EvidenceCoverageItem>> = {
    AFF: new Map(),
    NEG: new Map(),
  };
  const evidenceById = new Map(motion.evidenceLibrary.map((evidence) => [evidence.id, evidence]));

  for (const card of motion.constructiveCards) {
    const stage = motion.stages.find((item) => item.index === card.stageIndex);
    if (!stage) continue;
    for (const evidence of card.mandatoryEvidences) {
      evidenceItem(maps[stage.speakerSide], evidence, `环节 ${stage.index} ${card.targetRole}`);
    }
  }
  for (const sandbox of motion.sandboxCards) {
    for (const side of SIDES) {
      const perspective = sandbox.perspectives[side];
      for (const evidenceId of perspective?.mustHitEvidenceIds ?? []) {
        const evidence = evidenceById.get(evidenceId);
        if (evidence) evidenceItem(maps[side], evidence, `沙盘 ${sandbox.title}`);
      }
    }
  }

  const result = {} as Record<Side, SideEvidenceCoverage>;
  for (const side of SIDES) {
    const required = [...maps[side].values()];
    const checked = (manualEvidenceChecks?.[side] ?? []).filter((id) =>
      maps[side].has(id),
    );
    const missing = required.filter((item) => !checked.includes(item.evidenceId));
    const hitCount = checked.length;
    const percent = required.length === 0 ? 0 : Math.round((hitCount / required.length) * 100);
    result[side] = {
      side,
      required,
      checkedEvidenceIds: checked,
      requiredCount: required.length,
      hitCount,
      percent,
      missing,
    };
  }
  return result;
}

/* ------------------------------------------------------------------ */
/* 战报 Markdown 构建                                                  */
/* ------------------------------------------------------------------ */

export interface MatchReportOptions {
  /**
   * 教师跟场评分（本地录入，不要求后端持久化）。
   * 形状：participantId -> (rubric dimensionId -> 1~5 分)。
   */
  scores?: Record<string, Record<string, number>>;
  /** 生成时间（epoch 毫秒）；缺省用 Date.now()，测试可注入固定值 */
  generatedAt?: number;
}

function participantRoleLabel(role: string): string {
  return role === 'TEACHER' ? '教师' : '辩手';
}

function participantSideLabel(side: Side | undefined): string {
  return side ? sideLabel(side) : '—';
}

function stageSummaryLine(motion: MotionPackage, stageIndex: number, completed: boolean): string {
  const stage = motion.stages.find((item) => item.index === stageIndex);
  if (!stage) return `- ${completed ? '✅' : '⬜'} 环节 ${stageIndex}（赛程未定义）`;
  return `- ${completed ? '✅' : '⬜'} 环节 ${stage.index} · ${stage.name}（${stage.shortName}）`;
}

function revealVerificationLine(
  reveal: RevealedCard,
  state: ArenaUiState,
): string {
  const commitment = state.commitments[reveal.pairId]?.[reveal.side] ?? null;
  const verification = describeRevealVerification(reveal, commitment);
  return `- 校验状态：${verification.label}（${verification.detail}）`;
}

function renderRevealCard(
  motion: MotionPackage,
  state: ArenaUiState,
  reveal: RevealedCard,
): string[] {
  const summary = summarizeRevealedCard(reveal);
  const evidenceById = new Map(motion.evidenceLibrary.map((evidence) => [evidence.id, evidence]));
  const lines: string[] = [];
  lines.push(
    `- **${sideLabel(reveal.side)}** · 环节 ${reveal.stageIndex} · ${summary.kindLabel}：《${escapeMarkdownInline(
      summary.title,
    )}》`,
  );
  if (summary.targetRole) {
    lines.push(`  - 角色：${escapeMarkdownInline(summary.targetRole)}`);
  }
  if (summary.archetypeLabel) {
    lines.push(`  - 流派：${escapeMarkdownInline(summary.archetypeLabel)}`);
  }
  if (summary.modeLabel) {
    lines.push(`  - 模式：${escapeMarkdownInline(summary.modeLabel)}`);
  }
  if (summary.coreMission) {
    lines.push(`  - 核心指令：${escapeMarkdownInline(summary.coreMission)}`);
  }
  if (summary.questionChain) {
    lines.push('  - 三步题链：');
    lines.push(`    - ① 锁前提：${escapeMarkdownInline(summary.questionChain.step1LockPremise)}`);
    lines.push(`    - ② 逼两难：${escapeMarkdownInline(summary.questionChain.step2ForceDilemma)}`);
    lines.push(`    - ③ 夺判准：${escapeMarkdownInline(summary.questionChain.step3Crystallize)}`);
  }
  if (summary.sandbox) {
    lines.push(`  - 沙盘主题：${escapeMarkdownInline(summary.sandbox.topicLabel)}`);
    lines.push(`  - 视角：${escapeMarkdownInline(summary.sandbox.label)}`);
    lines.push(`  - 焦点问题：${escapeMarkdownInline(summary.sandbox.crossfireQuestion)}`);
    lines.push('  - 三步大纲：');
    for (const turn of summary.sandbox.outline) {
      lines.push(
        `    - 回合 ${turn.turn} · ${escapeMarkdownInline(turn.header)}：${escapeMarkdownInline(
          turn.keyClaim,
        )}`,
      );
      if (turn.counterToOpponent) {
        lines.push(`      - 预判反制：${escapeMarkdownInline(turn.counterToOpponent)}`);
      }
    }
    const mustHit = summary.sandbox.mustHitEvidenceTitles
      .map((evidenceId) => evidenceById.get(evidenceId)?.shortTitle ?? evidenceId)
      .map((title) => escapeMarkdownInline(title));
    if (mustHit.length > 0) {
      lines.push(`  - 必打数据：${mustHit.join('；')}`);
    }
  }
  if (summary.evidenceTitles.length > 0) {
    lines.push('  - 必打论据：');
    summary.evidenceTitles.forEach((title, index) => {
      const quote = summary.evidenceQuotes[index];
      lines.push(
        `    - ${escapeMarkdownInline(title)}${quote ? `（「${escapeMarkdownInline(quote)}」）` : ''}`,
      );
    });
  }
  if (summary.hiddenTrap) {
    lines.push(
      `  - 隐藏反杀锦囊：当${escapeMarkdownInline(summary.hiddenTrap.triggerCondition)} → ${escapeMarkdownInline(
        summary.hiddenTrap.counterResponse,
      )}`,
    );
  }
  if (summary.tabooWarning) {
    lines.push(`  - 禁忌提醒：${escapeMarkdownInline(summary.tabooWarning)}`);
  }
  if (summary.intelHint) {
    lines.push(`  - 情报提示：${escapeMarkdownInline(summary.intelHint)}`);
  }
  lines.push(`  - 揭示哈希：${markdownCell(reveal.hash)}（盐值 ${markdownCell(reveal.salt)}）`);
  lines.push(`  ${revealVerificationLine(reveal, state)}`);
  return lines;
}

function groupRevealsByPair(reveals: RevealedCard[]): { pairId: string; reveals: RevealedCard[] }[] {
  const groups = new Map<string, RevealedCard[]>();
  for (const reveal of reveals) {
    const key = reveal.pairId || `stage-${reveal.stageIndex}`;
    const list = groups.get(key) ?? [];
    list.push(reveal);
    groups.set(key, list);
  }
  const order = (side: Side): number => (side === 'AFF' ? 0 : 1);
  return [...groups.entries()]
    .map(([pairId, list]) => ({
      pairId,
      reveals: [...list].sort(
        (a, b) => a.stageIndex - b.stageIndex || order(a.side) - order(b.side),
      ),
    }))
    .sort((a, b) => (a.reveals[0]?.stageIndex ?? 0) - (b.reveals[0]?.stageIndex ?? 0));
}

function weightedScore(
  rubric: MotionPackage['coachRubric'],
  scores: Record<string, number>,
): number | null {
  let weighted = 0;
  let weightSum = 0;
  for (const dimension of rubric.dimensions) {
    const score = scores[dimension.id];
    if (typeof score !== 'number' || !Number.isFinite(score)) continue;
    weighted += score * dimension.weight;
    weightSum += dimension.weight;
  }
  if (weightSum <= 0) return null;
  return weighted / weightSum;
}

/**
 * 构建《纵横杯跟场诊断战报》Markdown。
 *
 * 覆盖：辩题/时间/参与人/环节完成情况/双盲揭示卡片/断言告警/必打证据覆盖/
 * 教师批注/跟场评分（若提供）/数据口径声明。所有用户可控文本均做 Markdown 转义。
 */
export function buildMatchReportMarkdown(
  motion: MotionPackage,
  state: ArenaUiState,
  options: MatchReportOptions = {},
): string {
  const generatedAt = options.generatedAt ?? Date.now();
  const scores = options.scores ?? {};
  const startedAt = state.startedAt ?? null;
  const finishedAt = state.finishedAt ?? null;
  const durationMs = startedAt ? Math.max(0, (finishedAt ?? generatedAt) - startedAt) : 0;
  const currentStage = state.stage ?? motion.stages.find((item) => item.index === state.stageIndex) ?? null;
  const debaters = state.participants.filter((participant) => participant.role === 'DEBATER');
  const coverage = buildEvidenceCoverage(motion, state.manualEvidenceChecks);
  const completed = new Set(state.completedStages);

  const lines: string[] = [];
  lines.push('# 纵横杯跟场诊断战报');
  lines.push('');
  lines.push('> 生成端：纵横演武场 Phase 1 · 教师上帝视角控制台');
  lines.push(`> 生成时间：${formatTimestamp(generatedAt)}`);
  lines.push(
    `> 数据口径：训练模拟语料（《${escapeMarkdownInline(motion.title)}》v${escapeMarkdownInline(
      motion.version,
    )}），仅用于队内训练复盘，不构成真实赛事判罚依据。`,
  );
  lines.push('');
  lines.push('## 一、比赛概览');
  lines.push('');
  lines.push('| 项目 | 内容 |');
  lines.push('| --- | --- |');
  lines.push(`| 辩题 | ${markdownCell(motion.motion)} |`);
  lines.push(`| 题库 | ${markdownCell(`${motion.title}（${motion.motionId} · v${motion.version}）`)} |`);
  lines.push(`| 房间 | ${markdownCell(state.roomId ?? '未连接房间')} |`);
  lines.push(`| 比赛状态 | ${markdownCell(MATCH_PHASE_LABEL[state.phase] ?? state.phase)} |`);
  lines.push(`| 开始时间 | ${formatTimestamp(startedAt)} |`);
  lines.push(`| 结束时间 | ${finishedAt ? formatTimestamp(finishedAt) : '—'} |`);
  lines.push(`| 比赛时长 | ${formatDurationMs(durationMs)} |`);
  lines.push(
    `| 环节进度 | ${completed.size} / ${motion.stages.length} 个环节已完成 |`,
  );
  lines.push(
    `| 当前环节 | ${
      currentStage
        ? markdownCell(`环节 ${currentStage.index} · ${currentStage.name}（${STAGE_PHASE_LABEL[state.stagePhase] ?? state.stagePhase}）`)
        : '尚未开始'
    } |`,
  );
  lines.push('');

  lines.push('## 二、参与人');
  lines.push('');
  if (state.participants.length === 0) {
    lines.push('未加入任何参与者。');
  } else {
    const connectionLabel = state.connection === 'connected' ? '在线' : '离线/未知';
    lines.push('| 姓名 | 角色 | 阵营 | 辩位 | 状态 |');
    lines.push('| --- | --- | --- | --- | --- |');
    for (const participant of state.participants) {
      lines.push(
        `| ${markdownCell(participant.name)} | ${participantRoleLabel(participant.role)} | ${participantSideLabel(
          participant.side,
        )} | ${participant.seat ? `${participant.seat} 辩` : '—'} | ${connectionLabel} |`,
      );
    }
  }
  lines.push('');

  lines.push('## 三、环节完成情况');
  lines.push('');
  lines.push(`已完成 ${completed.size} / ${motion.stages.length} 个环节。`);
  lines.push('');
  for (const stage of motion.stages) {
    lines.push(stageSummaryLine(motion, stage.index, completed.has(stage.index)));
  }
  if (completed.size === 0) {
    lines.push('');
    lines.push('（尚未完成任何环节）');
  }
  lines.push('');

  lines.push('## 四、双盲揭示卡片');
  lines.push('');
  if (state.reveals.length === 0) {
    lines.push('尚未揭示任何底牌。');
  } else {
    const groups = groupRevealsByPair(state.reveals);
    lines.push(`共揭示 ${state.reveals.length} 张卡片，覆盖 ${groups.length} 个双盲配对。`);
    lines.push('');
    for (const group of groups) {
      const stageRange = group.reveals
        .map((reveal) => reveal.stageIndex)
        .sort((a, b) => a - b)
        .filter((value, index, array) => array.indexOf(value) === index);
      lines.push(
        `### 配对 ${escapeMarkdownInline(group.pairId)}（环节 ${stageRange.join('、') || '—'}）`,
      );
      lines.push('');
      for (const reveal of group.reveals) {
        lines.push(...renderRevealCard(motion, state, reveal));
      }
      lines.push('');
    }
  }

  lines.push('## 五、断言告警');
  lines.push('');
  if (state.alerts.length === 0) {
    lines.push('未发现口径错误。');
  } else {
    const counts = { VIOLATION: 0, WARN: 0, INFO: 0 } as Record<string, number>;
    for (const alert of state.alerts) {
      counts[alert.severity] = (counts[alert.severity] ?? 0) + 1;
    }
    lines.push(
      `共 ${state.alerts.length} 条告警（违规 ${counts.VIOLATION ?? 0} · 警告 ${counts.WARN ?? 0} · 提示 ${
        counts.INFO ?? 0
      }）。`,
    );
    lines.push('');
    lines.push('| 环节 | 阵营 | 级别 | 命中原文 | 纠错口径 |');
    lines.push('| --- | --- | --- | --- | --- |');
    for (const alert of state.alerts) {
      lines.push(
        `| ${alert.stageIndex} | ${sideLabel(alert.side)} | ${
          SEVERITY_LABEL[alert.severity] ?? alert.severity
        } | ${markdownCell(alert.snippet)} | ${markdownCell(alert.message)} |`,
      );
    }
  }
  lines.push('');

  lines.push('## 六、必打论据覆盖');
  lines.push('');
  const coverageRows: string[] = [];
  for (const side of SIDES) {
    const sideCoverage = coverage[side];
    coverageRows.push(
      `| ${sideLabel(side)} | ${sideCoverage.requiredCount} | ${sideCoverage.hitCount} | ${sideCoverage.percent}% |`,
    );
  }
  if (coverage.AFF.requiredCount + coverage.NEG.requiredCount === 0) {
    lines.push('本题库未配置必打论据。');
  } else {
    lines.push('| 阵营 | 必打条数 | 已勾选 | 覆盖率 |');
    lines.push('| --- | --- | --- | --- |');
    lines.push(...coverageRows);
    lines.push('');
    for (const side of SIDES) {
      const sideCoverage = coverage[side];
      if (sideCoverage.missing.length === 0) {
        lines.push(`- ${sideLabel(side)}：全部必打论据已勾选。`);
      } else {
        lines.push(
          `- ${sideLabel(side)}未打（${sideCoverage.missing.length}）：${sideCoverage.missing
            .map((item) => escapeMarkdownInline(item.shortTitle))
            .join('；')}`,
        );
      }
    }
    lines.push('');
    lines.push(
      '> 口径：分母为本题库所有申论卡「必打论据」与沙盘视角「必打数据」的去重合集；分子为教师端手动勾选。',
    );
  }
  lines.push('');

  lines.push('## 七、教师批注');
  lines.push('');
  if (state.annotations.length === 0) {
    lines.push('暂无批注。');
  } else {
    for (const annotation of [...state.annotations].sort((a, b) => a.at - b.at)) {
      lines.push(renderAnnotationLine(annotation));
    }
  }
  lines.push('');

  lines.push('## 八、跟场评分');
  lines.push('');
  const rubric = motion.coachRubric;
  const hasScores = debaters.some((participant) => {
    const entry = scores[participant.id];
    return entry && Object.values(entry).some((value) => typeof value === 'number');
  });
  if (!hasScores || debaters.length === 0) {
    lines.push('本场未录入跟场评分。');
    if (rubric.dimensions.length > 0) {
      lines.push('');
      lines.push(
        `> 评分维度：${rubric.dimensions
          .map((dimension) => `${escapeMarkdownInline(dimension.label)}（权重 ${dimension.weight}）`)
          .join(' · ')}`,
      );
    }
  } else {
    lines.push('| 辩手 | 阵营 | 辩位 | 维度得分 | 加权总分 |');
    lines.push('| --- | --- | --- | --- | --- |');
    for (const participant of debaters) {
      const entry = scores[participant.id] ?? {};
      const dimensionScores = rubric.dimensions
        .map((dimension) => {
          const value = entry[dimension.id];
          return `${escapeMarkdownInline(dimension.label)} ${typeof value === 'number' ? value : '—'}`;
        })
        .join(' · ');
      const total = weightedScore(rubric, entry);
      lines.push(
        `| ${markdownCell(participant.name)} | ${participantSideLabel(participant.side)} | ${
          participant.seat ? `${participant.seat} 辩` : '—'
        } | ${markdownCell(dimensionScores)} | ${total === null ? '—' : total.toFixed(2)} |`,
      );
    }
    lines.push('');
    lines.push(
      `> 评分维度与权重：${rubric.dimensions
        .map((dimension) => `${escapeMarkdownInline(dimension.label)}（${dimension.weight}）`)
        .join(' · ')}；加权总分 = Σ(得分 × 权重) / Σ(已评分维度权重)。`,
    );
  }
  lines.push('');

  lines.push('## 九、数据口径与合规声明');
  lines.push('');
  lines.push(
    `1. 本战报由「纵横演武场」Phase 1 教师端离线生成，题库为训练模拟语料（《${escapeMarkdownInline(
      motion.title,
    )}》v${escapeMarkdownInline(motion.version)}），不代表真实赛事数据。`,
  );
  lines.push(
    '2. 双盲发牌采用 Commit-Reveal 承诺/揭示流程；演示/本地 Mock 数据使用可读占位哈希（demo-*），未执行真实 SHA-256 校验。',
  );
  lines.push(
    '3. 断言告警来自端侧确定性规则引擎（正则/关键词）与人工模拟转录，存在漏报与误报可能，请结合回放复核。',
  );
  lines.push(
    '4. 计时与环节完成情况以教师端权威状态机为准；本文件为纯文本 Markdown，可直接粘贴至复盘文档。',
  );
  lines.push('');
  return lines.join('\n');
}

/** 单条教师批注的 Markdown 行 */
export function renderAnnotationLine(annotation: RoomAnnotation): string {
  const stage = annotation.stageIndex > 0 ? `环节 ${annotation.stageIndex} · ` : '';
  return `- **[${stage}${formatTimeOfDay(annotation.at)}] ${escapeMarkdownInline(
    annotation.authorName,
  )}**：${escapeMarkdownInline(annotation.text)}`;
}

/* ------------------------------------------------------------------ */
/* 下载 / 复制（唯一允许触碰 DOM 的两个函数）                           */
/* ------------------------------------------------------------------ */

/**
 * 触发浏览器下载 Markdown 文件。
 * jsdom / SSR / 不支持 Blob URL 的环境下静默返回（不抛错）。
 */
export function downloadMarkdown(filename: string, content: string): void {
  if (typeof document === 'undefined') return;
  try {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const createObjectURL = globalThis.URL?.createObjectURL;
    if (typeof createObjectURL !== 'function') return;
    const url = createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    if (typeof globalThis.URL?.revokeObjectURL === 'function') {
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 1000);
    }
  } catch {
    /* 下载不可用时静默降级：调用方仍可展示预览文本 */
  }
}

/**
 * 复制 Markdown 到剪贴板；失败（无剪贴板权限 / 非安全上下文 / jsdom）返回 false。
 */
export async function copyMarkdown(content: string): Promise<boolean> {
  try {
    const clipboard = (globalThis as { navigator?: { clipboard?: { writeText?: (text: string) => Promise<void> } } })
      .navigator?.clipboard;
    if (!clipboard || typeof clipboard.writeText !== 'function') return false;
    await clipboard.writeText(content);
    return true;
  } catch {
    return false;
  }
}
